import React, { useState } from "react";
import CustomModal from "../../../../core/common/customModal";
import CustomToast from "../../../../core/common/customToast";
import { API } from "../../../../common/Constants";
import useFetch from "../../../../hooks/useFetch";
import { getAPIHeaders } from "../../../../common/Utils";
import CommonSelectV2 from "../../../../core/common/commonSelectV2";
import { DatePicker, TimePicker } from "antd";
import dayjs from "dayjs";
interface DownloadFormData {
  generateOMRAnswerSheet: boolean;
  generateAnswerKey: boolean;
  includeExamDateAndTime: boolean;
  examDate: string;
  examStartTime: string;
  examDuration: string;
  marksPerQuestion: string;
  negativeMarksPerQuestion: string;
  includeInstitutionDetails: boolean;
  includeAddress: boolean;
  includeContactDetails: boolean;
  generalInstructions: string;
  markingSchema: string;
  importantNotes: string;
  candidateInstructions: string;
}

interface AssessmentDownloadModalProps {
  show: boolean;
  onClose: () => void;
  onDownload: () => void;
  downloadFormData: DownloadFormData;
  setDownloadFormData: React.Dispatch<React.SetStateAction<DownloadFormData>>;
  assessmentId: string;
}

const AssessmentDownloadModal: React.FC<AssessmentDownloadModalProps> = ({
  show,
  onClose,
  onDownload,
  downloadFormData,
  setDownloadFormData,
  assessmentId,
}) => {
  const [templates, setTemplates] = useState<string[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<string>("");
  const [isLoadingTemplates, setIsLoadingTemplates] = useState<boolean>(false);
  const [isLoadingTemplateDetails, setIsLoadingTemplateDetails] = useState<boolean>(false);
  const [toastSettings, setToastSettings] = useState<{
    show: boolean;
    message: string;
    heading: string;
    type: "ERROR" | "SUCCESS" | "INFO" | "";
  }>({
    show: false,
    message: "",
    heading: "",
    type: "ERROR",
  });
  const { callFetch } = useFetch();

  const onToastClose = () => {
    setToastSettings({
      show: false,
      message: "",
      heading: "",
      type: "",
    });
  };

  const fetchTemplates = React.useCallback(async () => {
    setIsLoadingTemplates(true);
    try {
      const response = await callFetch(
        API.ASSESSMENT_DOWNLOAD_TEMPLATES,
        'GET',
        getAPIHeaders(),
        null,
        null
      );
      if (response && Array.isArray(response)) {
        setTemplates(response);
      }
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    } finally {
      setIsLoadingTemplates(false);
    }
  }, []);

  // Fetch available templates when modal opens
  React.useEffect(() => {
    if (show && templates.length === 0) {
      fetchTemplates();
    }
  }, [show, fetchTemplates, templates.length]);

  const handleTemplateSelect = async (templateName: string) => {
    setSelectedTemplate(templateName);
    setIsLoadingTemplateDetails(true);
    
    try {
      const response = await callFetch(
        API.ASSESSMENT_TEMPLATE_DETAILS
          .replace(':assessmentId', assessmentId)
          .replace(':templateName', templateName),
        'GET',
        getAPIHeaders(),
        null,
        null
      );
      
      if (response) {
        // Prepopulate form with template details
        const templateData = {
          // Assessment details
          examDuration: response.assessmentDetails?.duration?.toString() || "",
          marksPerQuestion: response.assessmentDetails?.totalMarks ? 
            Math.round(response.assessmentDetails.totalMarks / response.assessmentDetails.totalQuestions).toString() : "",
          
          // Boolean flags
          includeExamDateAndTime: response.includeExamDateAndTime || false,
          generateOMRAnswerSheet: response.includeOMRAnswerSheet || false,
          includeInstitutionDetails: response.includeInstitutionDetails || false,
          includeAddress: response.includeAddress || false,
          includeContactDetails: response.includeContactDetails || false,
          
          // Instructions - join arrays with /n for textarea format
          generalInstructions: response.examInstructions?.generalInstructions?.join('\n') || "",
          markingSchema: response.examInstructions?.markingScheme?.join('\n') || "",
          importantNotes: response.examInstructions?.importantNotes?.join('\n') || "",
          candidateInstructions: response.examInstructions?.candidateInstructions?.join('\n') || "",
          
          // Keep existing values for fields not in response
          generateAnswerKey: downloadFormData.generateAnswerKey,
          examDate: downloadFormData.examDate,
          examStartTime: downloadFormData.examStartTime,
          negativeMarksPerQuestion: downloadFormData.negativeMarksPerQuestion
        };
        
        setDownloadFormData(templateData);
      }
    } catch (error) {
      console.error('Failed to fetch template details:', error);
    } finally {
      setIsLoadingTemplateDetails(false);
    }
  };

  const validateForm = () => {
    if (downloadFormData.includeExamDateAndTime) {
      if (!downloadFormData.examDate) {
        setToastSettings({
          show: true,
          message: "Exam Date is required when including exam date and time.",
          heading: "Validation Error",
          type: "ERROR",
        });
        return false;
      }
      if (!downloadFormData.examStartTime) {
        setToastSettings({
          show: true,
          message: "Exam Start Time is required when including exam date and time.",
          heading: "Validation Error",
          type: "ERROR",
        });
        return false;
      }
      if (!downloadFormData.examDuration) {
        setToastSettings({
          show: true,
          message: "Exam Duration is required when including exam date and time.",
          heading: "Validation Error",
          type: "ERROR",
        });
        return false;
      }
    }
    return true;
  };

  const handleDownload = () => {
    if (validateForm()) {
      onDownload();
    }
  };
  return (
    <>
      <CustomToast
        show={toastSettings?.show}
        message={toastSettings?.message}
        heading={toastSettings?.heading}
        type={toastSettings?.type || "INFO"}
        onClose={onToastClose} />
      <CustomModal
        show={show}
        title="Download Assessment Configuration"
        onClose={onClose}
        size="xl"
              bodyContent={
          <div className="row">
            <div className="col-12 mb-3">
              <div className="form-group">
                <label className="form-label">Select Template</label>
                <CommonSelectV2
                  options={templates.map(template => ({ value: template, label: template }))}
                  value={selectedTemplate ? { value: selectedTemplate, label: selectedTemplate } : null}
                  onChange={(value: string) => handleTemplateSelect(value)}
                  disabled={isLoadingTemplates || isLoadingTemplateDetails}
                  placeHolder="Choose a template..."
                />
                {isLoadingTemplates && (
                  <small className="text-muted">
                    <i className="fa fa-spinner fa-spin me-1"></i>Loading templates...
                  </small>
                )}
                {isLoadingTemplateDetails && (
                  <small className="text-muted">
                    <i className="fa fa-spinner fa-spin me-1"></i>Loading template details...
                  </small>
                )}
              </div>
            </div>
            <div className="col-md-6">
              <h6 className="mb-3">General Settings</h6>
            <div className="mb-3">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="generateOMRAnswerSheet"
                  checked={downloadFormData.generateOMRAnswerSheet}
                  onChange={(e) => setDownloadFormData(prev => ({ ...prev, generateOMRAnswerSheet: e.target.checked }))}
                />
                <label className="form-check-label" htmlFor="generateOMRAnswerSheet">
                  Generate OMR Answer Sheet
                </label>
              </div>
            </div>
            <div className="mb-3">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="generateAnswerKey"
                  checked={downloadFormData.generateAnswerKey}
                  onChange={(e) => setDownloadFormData(prev => ({ ...prev, generateAnswerKey: e.target.checked }))}
                />
                <label className="form-check-label" htmlFor="generateAnswerKey">
                  Show Correct Answers
                </label>
              </div>
            </div>
            <div className="mb-3">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="includeExamDateAndTime"
                  checked={downloadFormData.includeExamDateAndTime}
                  onChange={(e) => setDownloadFormData(prev => ({ ...prev, includeExamDateAndTime: e.target.checked }))}
                />
                <label className="form-check-label" htmlFor="includeExamDateAndTime">
                  Include Exam Date and Time
                </label>
              </div>
            </div>
            {downloadFormData.includeExamDateAndTime && (
              <div className="row mb-3">
                <div className="col-12 mb-2">
                  <small className="text-muted">
                    <i className="ti ti-info-circle me-1"></i>
                    When including exam date and time, all three fields below are required.
                  </small>
                </div>
                <div className="col-md-4">
                  <label className="form-label">Exam Date <span className="text-danger">*</span></label>
                  <DatePicker
                    className="w-100"
                    value={downloadFormData.examDate ? dayjs(downloadFormData.examDate) : null}
                    onChange={(date, dateString) => setDownloadFormData(prev => ({ ...prev, examDate: typeof dateString === 'string' ? dateString : '' }))}
                    format="YYYY-MM-DD"
                    status={!downloadFormData.examDate ? 'error' : ''}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Exam Start Time <span className="text-danger">*</span></label>
                  <TimePicker
                    className="w-100"
                    value={downloadFormData.examStartTime ? dayjs(`2000-01-01T${downloadFormData.examStartTime}`) : null}
                    onChange={(time, timeString) => setDownloadFormData(prev => ({ ...prev, examStartTime: typeof timeString === 'string' ? timeString : '' }))}
                    format="HH:mm"
                    status={!downloadFormData.examStartTime ? 'error' : ''}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Duration (minutes) <span className="text-danger">*</span></label>
                  <input
                    type="number"
                    className={`form-control ${!downloadFormData.examDuration ? 'is-invalid' : ''}`}
                    placeholder="Enter duration in minutes"
                    value={downloadFormData.examDuration}
                    onChange={(e) => setDownloadFormData(prev => ({ ...prev, examDuration: e.target.value }))}
                    required
                  />
                  {!downloadFormData.examDuration && (
                    <div className="invalid-feedback">
                      Exam duration is required when including exam date and time.
                    </div>
                  )}
                </div>
              </div>
            )}
            {!downloadFormData.includeExamDateAndTime && (
              <div className="mb-3">
                <label className="form-label">Exam Duration (in minutes)</label>
                <input
                  type="number"
                  className="form-control"
                  placeholder="Optional"
                  value={downloadFormData.examDuration}
                  onChange={(e) => setDownloadFormData(prev => ({ ...prev, examDuration: e.target.value }))}
                />
              </div>
            )}
          </div>
          <div className="col-md-6">
            <h6 className="mb-3">Marks & Institution Settings</h6>
            <div className="row mb-3">
              <div className="col-md-6">
                <label className="form-label">Marks Per Question</label>
                <input
                  type="number"
                  className="form-control"
                  placeholder="Optional"
                  value={downloadFormData.marksPerQuestion}
                  onChange={(e) => setDownloadFormData(prev => ({ ...prev, marksPerQuestion: e.target.value }))}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Negative Marks Per Question</label>
                <input
                  type="number"
                  className="form-control"
                  placeholder="Optional"
                  value={downloadFormData.negativeMarksPerQuestion}
                  onChange={(e) => setDownloadFormData(prev => ({ ...prev, negativeMarksPerQuestion: e.target.value }))}
                />
              </div>
            </div>
            <div className="mb-3">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="includeInstitutionDetails"
                  checked={downloadFormData.includeInstitutionDetails}
                  onChange={(e) => setDownloadFormData(prev => ({ ...prev, includeInstitutionDetails: e.target.checked }))}
                />
                <label className="form-check-label" htmlFor="includeInstitutionDetails">
                  Include Institution Details
                </label>
              </div>
            </div>
            <div className="mb-3">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="includeAddress"
                  checked={downloadFormData.includeAddress}
                  onChange={(e) => setDownloadFormData(prev => ({ ...prev, includeAddress: e.target.checked }))}
                />
                <label className="form-check-label" htmlFor="includeAddress">
                  Include Address
                </label>
              </div>
            </div>
            <div className="mb-3">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="includeContactDetails"
                  checked={downloadFormData.includeContactDetails}
                  onChange={(e) => setDownloadFormData(prev => ({ ...prev, includeContactDetails: e.target.checked }))}
                />
                <label className="form-check-label" htmlFor="includeContactDetails">
                  Include Contact Details
                </label>
              </div>
            </div>
          </div>
          <div className="col-12">
            <h6 className="mb-3">Instructions & Notes</h6>
            <div className="row">
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">General Instructions</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    placeholder="Enter instructions with /n for new lines"
                    value={downloadFormData.generalInstructions}
                    onChange={(e) => setDownloadFormData(prev => ({ ...prev, generalInstructions: e.target.value }))}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Marking Schema</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    placeholder="Enter marking schema with /n for new lines"
                    value={downloadFormData.markingSchema}
                    onChange={(e) => setDownloadFormData(prev => ({ ...prev, markingSchema: e.target.value }))}
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Important Notes</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    placeholder="Enter important notes with /n for new lines"
                    value={downloadFormData.importantNotes}
                    onChange={(e) => setDownloadFormData(prev => ({ ...prev, importantNotes: e.target.value }))}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Candidate Instructions</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    placeholder="Enter candidate instructions with /n for new lines"
                    value={downloadFormData.candidateInstructions}
                    onChange={(e) => setDownloadFormData(prev => ({ ...prev, candidateInstructions: e.target.value }))}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      }
      footerContent={
        <div className="d-flex justify-content-end gap-2">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleDownload}
          >
            Download
          </button>
        </div>
      }
    />
    </>
  );
};

export default AssessmentDownloadModal;
